import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { BehaviorSubject } from 'rxjs';

import { LocalStorageService } from '@recruitee/common';
import { uniqByKey } from '@recruitee/utils';
import { WalkthroughStepsService } from '@recruitee/walkthrough';

import {
  RecentNavigationSnapshot,
  ResourceKind,
  SnapshotResolver,
  VisitedEntityBase,
} from './recent-navigation.types';

@Injectable({
  providedIn: 'root',
})
export class VisitedEntitiesHistoryService<T extends VisitedEntityBase<ResourceKind, {}>> {
  public static readonly lsKey = 'visited-entities-history';
  private static readonly limit = 10;
  public visitedEntities$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>(
    this.localStorageService.get(VisitedEntitiesHistoryService.lsKey, []),
  );

  constructor(
    private localStorageService: LocalStorageService,
    private walkthroughStepsService: WalkthroughStepsService,
  ) {
    this.visitedEntities$.subscribe(entities =>
      this.localStorageService.set(VisitedEntitiesHistoryService.lsKey, entities),
    );
  }

  public updateHistory(entity: T): void {
    if (!this.walkthroughStepsService.activeStep()) {
      const entities = uniqByKey([entity, ...this.visitedEntities$.getValue()], 'id').splice(
        0,
        VisitedEntitiesHistoryService.limit,
      );
      this.visitedEntities$.next(entities);
    }
  }
}

export function saveNavigationHistory(resolver: SnapshotResolver<RecentNavigationSnapshot>) {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const resultResolver = resolver(route, state);
    const historyService = inject(VisitedEntitiesHistoryService);

    resultResolver.subscribe(result => {
      historyService.updateHistory(result);

      return result;
    });
  };
}
